// API クライアント
// 開発時は Vite が /api を Pages Functions (port 8788) にプロキシ

export type JobStatus =
  | "created"
  | "uploading"
  | "processing"
  | "completed"
  | "failed";

export type JobFile = {
  id: string;
  job_id: string;
  file_name: string;
  r2_key: string;
  status: string;
  ocr_text: string | null;
  translated_text: string | null;
  sort_order: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

export type JobWithFiles = {
  id: string;
  status: JobStatus;
  source_lang: string | null;
  target_lang: string | null;
  output_format: string;
  total_files: number;
  completed_files: number;
  result_r2_key: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  files: JobFile[];
};

const BASE = "/api";

function getStoredPassword(): string | null {
  return localStorage.getItem("ocr_access_password");
}

function withAuth(init?: RequestInit): RequestInit {
  const password = getStoredPassword();
  const headers = new Headers(init?.headers);
  if (password) headers.set("X-Access-Password", password);
  return { ...init, headers };
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, withAuth(init));
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API error ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function createJob(params: {
  source_lang: string;
  target_lang: string;
  output_format: string;
}): Promise<{ job_id: string }> {
  return jsonFetch(`${BASE}/jobs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
}

export async function uploadFile(
  jobId: string,
  file: File,
  sortOrder: number,
): Promise<{ file_id: string }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("sort_order", String(sortOrder));
  return jsonFetch(`${BASE}/jobs/${jobId}/upload`, {
    method: "POST",
    body: fd,
  });
}

export async function startJob(jobId: string): Promise<void> {
  await jsonFetch(`${BASE}/jobs/${jobId}/start`, { method: "POST" });
}

export async function processFile(
  jobId: string,
  fileId: string,
): Promise<{ ok: boolean; durationMs: number; error?: string }> {
  return jsonFetch(`${BASE}/jobs/${jobId}/process-file`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ file_id: fileId }),
  });
}

export async function finalizeJob(jobId: string): Promise<void> {
  await jsonFetch(`${BASE}/jobs/${jobId}/finalize`, { method: "POST" });
}

export async function retryFile(
  jobId: string,
  fileId: string,
): Promise<{ ok: boolean; durationMs: number; error?: string }> {
  return jsonFetch(`${BASE}/jobs/${jobId}/retry-file`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ file_id: fileId }),
  });
}

export async function getJob(jobId: string): Promise<JobWithFiles> {
  return jsonFetch(`${BASE}/jobs/${jobId}`);
}

export function downloadUrl(jobId: string): string {
  return `${BASE}/jobs/${jobId}/download`;
}

/**
 * 認証ヘッダ付きでダウンロード → blob 化 → ブラウザでファイル保存
 * (素のリンクだと X-Access-Password が付かず 401 になるため)
 */
export async function downloadResult(jobId: string): Promise<void> {
  const res = await fetch(downloadUrl(jobId), withAuth());
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Download failed (${res.status}): ${text}`);
  }

  // Content-Disposition から元のファイル名を抽出
  const cd = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(cd);
  const filename = match ? match[1] : `ocr-result-${jobId}.md`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type JobSummary = {
  id: string;
  status: JobStatus;
  source_lang: string | null;
  target_lang: string | null;
  output_format: string;
  total_files: number;
  completed_files: number;
  result_r2_key: string | null;
  created_at: string;
  updated_at: string;
};

export async function listJobs(): Promise<{ jobs: JobSummary[] }> {
  return jsonFetch(`${BASE}/jobs`);
}
